import 'vite/modulepreload-polyfill';
import gsap from 'gsap';
import Swiper from 'swiper';
import { Autoplay, EffectFade, Scrollbar } from 'swiper/modules';
import { createApp, onMounted, ref } from 'vue';

import { ANIMATION_CONFIGS } from '~/constants/animations';

import * as AnalyticsData from '../analytics/analytics-data';
import { BaseSection } from './base-section';

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const SWIPER_SETTINGS = {
  autoplay: {
    delay: 5000,
  },
  effect: 'fade',
  fadeEffect: {
    crossFade: true,
  },
  loop: true,
  modules: [Autoplay, EffectFade, Scrollbar],
  on: {
    autoplayTimeLeft: function (swiper, _, percentage) {
      if (swiper.scrollbar.dragEl) {
        const maxWidth = 100 / swiper.slides.length;
        swiper.scrollbar.dragEl.style.width = (1 - percentage) * maxWidth + '%';
      }
    },
  },
  scrollbar: {
    draggable: true,
    el: '.swiper-scrollbar',
    hide: false,
  },
};

const SECTION_NAME = 'inspiration-carousel';

/**
 * Inspiration Carousel Section
 *
 * This section displays a product inspiration carousel with autoplay functionality.
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */ class InspirationCarousel extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const autoplayDelay = Number(container.getAttribute('data-autoplay-delay'));
    const sectionInstance = this;

    createApp({
      name: 'InspirationCarouselSection',
      setup() {
        const carousel = ref(null);
        const isPlaying = ref(true);
        const hoverTarget = ref(null);

        const handleHoverButton = (buttonId) => {
          hoverTarget.value = buttonId;

          if (hoverTarget.value) {
            carousel.value.autoplay.pause();
          } else {
            carousel.value.autoplay.resume();
          }
        };

        const togglePlayPause = () => {
          isPlaying.value = !isPlaying.value;
          carousel.value.autoplay[isPlaying.value ? 'resume' : 'pause']();
        };

        onMounted(() => {
          new IntersectionObserver((entries) => {
            const isVisible = entries[0].isIntersecting;

            if (isVisible && !carousel.value) {
              // Initialize Swiper when it first becomes visible
              carousel.value = new Swiper(
                document.querySelector(`[data-section-type="${SECTION_NAME}"]`),
                {
                  ...SWIPER_SETTINGS,
                  autoplay: {
                    delay: autoplayDelay,
                    disableOnInteraction: false,
                  },
                },
              );
            } else if (carousel.value) {
              // Pause when leaving viewport, resume when entering (if it was playing)
              if (isVisible && isPlaying.value) {
                carousel.value.autoplay.resume();
              } else {
                carousel.value.autoplay.pause();
              }
            }
          }).observe(container);

          // Hook analytics event to CTA links
          if (sectionInstance.isHomePageTemplate()) {
            AnalyticsData.hookHomepageModuleClicked({
              sectionName: SECTION_NAME,
            });
          }
        });

        return {
          handleHoverButton,
          hoverTarget,
          isPlaying,
          togglePlayPause,
        };
      },
    }).mount(container);
  }

  onUnload(instanceData) {
    // Kill ScrollTrigger instances
    if (instanceData.animation && instanceData.animation.scrollTrigger) {
      instanceData.animation.scrollTrigger.kill();
    }

    // Kill the animation
    if (instanceData.animation) {
      instanceData.animation.kill();
      instanceData.animation = null;
    }
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    if (!container) return null;

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) return null;

    tl.fromTo(container, { opacity: 0 }, ANIMATION_CONFIGS.FADE_IN);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new InspirationCarousel();
});
